import { formatDateRange } from '@mntn/formatters'
import { Image } from '@mntn/select-components'
import { AspectRatio, Text } from '@mntn/ui-components'
import { useMemo } from 'react'
import { Badge } from '@/components/shared'
import { AllocationNotify } from '@/components/shared/allocation-notify'
import { SpendMeter } from '@/components/shared/spend-meter'
import { getOfferingText, hasSpendError } from '@/components/shared/utils'
import type { OrderItem } from '@/flight-deck-api/services/order-items.service'

interface Props {
  // todo: just pass in needed values from order item
  selectedOffering: OrderItem
}

// biome-ignore lint/plugin: RETIRED, do not copy. Convert to a named function export when touching this.
export const OfferingSummarySection: React.FC<Props> = ({ selectedOffering }) => {
  const { airStartTime, airEndTime, programs, budgetMetrics } = selectedOffering
  const {
    delivered: spendAmount,
    total: totalBudget,
    unallocated: unallocatedBudget,
    ineffective: ineffectiveBudget,
    effective: effectiveBudget,
  } = budgetMetrics
  const airDateRange = formatDateRange(airStartTime, airEndTime)

  const programNames = useMemo(() => programs.map(program => program.programName), [programs])
  const offeringText = getOfferingText(programs.length)

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-6">
        <div className="w-40">
          <AspectRatio ratio={4 / 5}>
            <Image
              src={selectedOffering.coverMediaUrl || ''}
              alt={selectedOffering.offeringName}
              className="w-full h-full object-cover bg-sunken-surface-bg"
            />
          </AspectRatio>
        </div>
        <div className="flex flex-col flex-1 pt-4 min-w-0 justify-between">
          <div className="flex flex-col gap-2">
            {airDateRange && <Text variant="base-sb">Deal valid: {airDateRange}</Text>}

            <Text variant="base-sm" className="truncate text-secondary">
              {offeringText}
            </Text>

            <div className="flex flex-wrap gap-2 py-1">
              {programNames.map(name => (
                <Badge key={name} label={name} type="secondary" variant="subtle" shape="rounded" />
              ))}
            </div>
          </div>

          <SpendMeter
            spendAmount={spendAmount}
            totalBudget={totalBudget}
            hasSpendError={hasSpendError(selectedOffering)}
          />
        </div>
      </div>

      <AllocationNotify
        effectiveBudget={effectiveBudget}
        ineffectiveBudget={ineffectiveBudget}
        unallocatedBudget={unallocatedBudget}
      />
    </div>
  )
}
